import { create } from "zustand";
import {
  BackendLineDirection,
  normalizeBackendDirection,
} from "../../EditLineCrossings/EditLineCrossingsUtils/lineDirection";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { AxiosError } from "axios";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { getNumbersAfterEditCamera, getNumbersAfterSites } from "../EditCamera";
import { CameraStreams, CameraStream } from "../editCameraInterfaces";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  UpdatedAlertStreamTypes,
  UpdatedAlertStreams,
} from "../EditCameraUtils/editCameraInterfaces";
import { AutoPatrolSchedule } from "../../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { LINE_COLORS } from "../../EditLineCrossings/EditLineCrossingsUtils/lineColors";
import { buildLineCrossingsListPath } from "../../EditLineCrossings/EditLineCrossingsUtils/lineCrossingsListPath";
import {
  DEFAULT_CUSTOM_ANALYSIS_NAME,
  DEFAULT_VLM_SETTINGS,
  VlmSettings,
} from "../VlmTabToggles";
import {
  buildVlmConfigPatch,
  mapVlmConfigToUi,
} from "../EditCameraUtils/vlmConfigMappers";
import { getCameraVlmConfig } from "../EditCameraUtils/getCameraVlmConfig";
import { getAllVlmCases, getVlmCases } from "../EditCameraUtils/getVlmCases";

/** `permission.super_user` from the login-time user/info in localStorage. */
const isSuperUserFromLocalStorage = (): boolean => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    return userInfo?.permission?.super_user === true;
  } catch {
    return false;
  }
};
import { patchCameraVlmConfig as patchCameraVlmConfigRequest } from "../EditCameraUtils/patchCameraVlmConfig";
import {
  getModalityIdsFromConfig,
  resolveVlmCaseValueMap,
  VlmCaseValueMap,
} from "../EditCameraUtils/vlmCaseRegistry";

// Line crossing types (shared with editLineCrossingsStore)
export type LineDirection = "AtoB" | "BtoA" | "Both";
export type { BackendLineDirection };

export interface LineCrossing {
  id?: number;
  camera: number;
  metric_labels: number[];
  coordinates: [[number, number], [number, number]];
  direction: LineDirection;
  /** Preset ("configuration") the line belongs to; null/undefined = camera-wide Default line. */
  preset?: number | null;
  color: string;
}

export interface MetricLabel {
  id: number;
  label_name: string;
}

/**
 * Determines if Side A is on the left side of the screen based on line coordinates.
 */
const isSideAOnLeft = (
  coordinates: [[number, number], [number, number]]
): boolean => {
  const [point1, point2] = coordinates;
  const dx = point2[0] - point1[0];
  const dy = point2[1] - point1[1];

  if (dy !== 0) {
    return dy < 0;
  } else {
    return dx < 0;
  }
};

/**
 * Converts backend direction format to frontend format.
 */
const backendToFrontendDirection = (
  rawBackendDirection: string,
  coordinates: [[number, number], [number, number]]
): LineDirection => {
  // ENG-1317: case-insensitive; see normalizeBackendDirection.
  const backendDirection = normalizeBackendDirection(rawBackendDirection);
  if (backendDirection === "both") return "Both";

  const sideAIsLeft = isSideAOnLeft(coordinates);

  if (backendDirection === "l2r") {
    return sideAIsLeft ? "AtoB" : "BtoA";
  } else {
    return sideAIsLeft ? "BtoA" : "AtoB";
  }
};

/**
 * Checks if a direction string is in the legacy frontend format.
 */
const isLegacyDirectionFormat = (direction: string): boolean => {
  return direction === "AtoB" || direction === "BtoA" || direction === "Both";
};

interface EditCameraStore {
  selectedAlertId: string;
  selectedAlertType: string;
  setSelectedAlertId: (selectedAlertId: string) => void;
  setSelectedAlertType: (selectedAlertType: string) => void;
  notesDialogEditCameraOpen: boolean;
  setNotesDialogEditCameraOpen: (notesDialogEditCameraOpen: boolean) => void;
  notesText: string;
  setNotesText: (notesText: string) => void;
  streamChangesMade: boolean;
  setStreamChangesMade: (streamChangesMade: boolean) => void;
  enveraUser: boolean;
  siteInfoEditCamera: any;
  setSiteInfoEditCamera: (siteInfo: any) => void;
  chmActive: boolean;
  setChmActive: (chmActive: boolean) => void;
  lineCrossingActive: { intruder: boolean; vehicle: boolean };
  setLineCrossingActive: (lineCrossingActive: {
    intruder: boolean;
    vehicle: boolean;
  }) => void;
  // Line crossings data
  lineCrossingsData: LineCrossing[];
  setLineCrossingsData: (lineCrossingsData: LineCrossing[]) => void;
  lineCrossingsMetricLabels: MetricLabel[];
  setLineCrossingsMetricLabels: (metricLabels: MetricLabel[]) => void;
  getLineCrossingsData: (
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;
  addHealthMonitoringDialogOpen: boolean;
  setAddHealthMonitoringDialogOpen: (
    addHealthMonitoringDialogOpen: boolean
  ) => void;
  chmValueChanged: boolean;
  deleteWebhookDialogOpen: boolean;
  deleteAlertDialogOpen: boolean;
  setDeleteAlertDialogOpen: (deleteAlertDialogOpen: boolean) => void;
  webhookIdToDelete: string;
  setWebhookIdToDelete: (webhookIdToDelete: string) => void;
  backdropLoadingScreenEditCameraOn: boolean;
  setBackdropLoadingScreenEditCameraOn: (
    backdropLoadingScreenEditCameraOn: boolean
  ) => void;
  setDeleteWebhookDialogOpen: (deleteWebhookDialogOpen: boolean) => void;
  setCHMValueChanged: (chmValueChanged: boolean) => void;
  showHealthMonitoring: boolean;
  setShowHealthMonitoring: (showHealthMonitoring: boolean) => void;
  chmStreamMetric: any;
  setCHMStreamMetric: (chmStreamMetric: any) => void;
  cameraStreams: Array<{
    id: number;
    camera: number;
    name: string;
    product: string;
  }> | null;
  setCameraStreams: (cameraStreams: EditCameraStore["cameraStreams"]) => void;
  cameraStreamsSelectedIndex: number;
  setCameraStreamsSelectedIndex: (cameraStreamsSelectedIndex: number) => void;
  sysaidAlertsOnCamera: boolean;
  setSysaidAlertsOnCamera: (sysaidAlertsOnCamera: boolean) => void;
  addSysaidAlertDialogOpen: boolean;
  setAddSysaidAlertDialogOpen: (addSysaidAlertDialogOpen: boolean) => void;
  webhooksData: Array<{
    id: number;
    stream: Pick<CameraStream, "id" | "name"> & {
      camera: number;
      product: string;
    };
    webhook: {
      id: number;
      customer: {
        id: number;
        customer_id: string;
        deployment_id: string | null;
        read_filename: boolean;
        read_filename_expression: string | null;
        new_relic_muting_rule_id: string | null;
        ecs_task_id: string | null;
        active: boolean;
        container_name: string;
        name: string;
        lead: string;
        integration_type: string;
      };
      display_name: string;
      webhook_type: string;
      target_url: string;
      method: string;
      auth_type: string | null;
      auth_endpoint: string | null;
      auth_body: string | null;
      auth_token_path: string | null;
      message_body: string;
    };
    custom_fields: Record<string, any>;
    event_types: string[];
  }> | null;
  setWebhooksData: (webhooksData: EditCameraStore["webhooksData"]) => void;
  cameraStreamsObject: Partial<CameraStreams>;
  setCameraStreamsObject: (cameraStreamsObject: Partial<CameraStreams>) => void;
  cameraStreamsPatchObject: Partial<CameraStreams>;
  setCameraStreamsPatchObject: (
    cameraStreamsPatchObject:
      | Partial<CameraStreams>
      | ((prev: Partial<CameraStreams>) => Partial<CameraStreams>)
  ) => void;
  updatedAlertStreamTypes: UpdatedAlertStreamTypes;
  updatedAlertStreams: UpdatedAlertStreams;
  setUpdatedAlertStreams: (
    updatedAlertStreams:
      | EditCameraStore["updatedAlertStreams"]
      | ((
          prev: EditCameraStore["updatedAlertStreams"]
        ) => EditCameraStore["updatedAlertStreams"])
  ) => void;
  setUpdatedAlertStreamTypes: (
    updatedAlertStreamTypes:
      | UpdatedAlertStreamTypes
      | ((prev: UpdatedAlertStreamTypes) => UpdatedAlertStreamTypes)
  ) => void;
  vCHOnlySite: boolean;
  setVCHOnlySite: (vCHOnlySite: boolean) => void;
  slicingChangesMade: boolean;
  setSlicingChangesMade: (slicingChangesMade: boolean) => void;
  slicingChanges: {
    streamId: number;
    slice: boolean;
    streamIndex: number;
    max_slices?: number;
  }[];
  setSlicingChanges: (
    slicingChanges: {
      streamId: number;
      slice: boolean;
      streamIndex: number;
      max_slices?: number;
    }[]
  ) => void;
  showSlicingWarningMessage: boolean;
  setShowSlicingWarningMessage: (showSlicingWarningMessage: boolean) => void;
  handleDeleteWebhook: (
    navigate: NavigateFunction,
    webhookId: string
  ) => Promise<void>;
  handleSubmitNewStreamWebhook: (
    postObject: any,
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;
  getWebhooksData: (
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;
  handlePatchStreamWebhook: (
    postObject: any,
    webhookId: number,
    navigate: NavigateFunction
  ) => Promise<void>;
  getCameraStreams: (
    navigate: NavigateFunction,
    cameraId: number
  ) => Promise<void>;
  handlePatchCameraStreams: (
    navigate: NavigateFunction,
    cameraId: number,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handleSubmitNewSysaidAlert: (
    postObject: any,
    setCameraGeneralInfo: any,
    setCameraGeneralInfoBackup: any,
    setPatchObject: any,
    cameraGeneralInfoBackup: any,
    navigate: NavigateFunction
  ) => Promise<void>;
  handlePatchImmixAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchDiceAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchBoldAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchPatriotAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>,
    setPatriotAlerts: Dispatcher<any>
  ) => Promise<void>;
  handlePatchSureviewAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchSentinelAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchSoftguardAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchSysaidAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handlePatchEvalinkAlerts: (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => Promise<void>;
  handleDeleteAlerts: (navigate: NavigateFunction) => Promise<void>;
  handleCheckForVCHOnlySite: (navigate: NavigateFunction) => Promise<void>;
  handlePatchSlicing: (
    streamId: number,
    slice: boolean,
    streamIndex: number,
    navigate: NavigateFunction,
    max_slices?: number
  ) => Promise<boolean>;
  vlmSettings: VlmSettings;
  vlmSettingsBackup: VlmSettings;
  vlmCustomCase: string;
  vlmCustomCaseBackup: string;
  vlmConfigLoading: boolean;
  vlmConfigLoaded: boolean;
  vlmConfigCameraId: number | null;
  vlmCaseValueMap: VlmCaseValueMap;
  vlmChangesMade: boolean;
  setVlmChangesMade: (vlmChangesMade: boolean) => void;
  fetchCameraVlmConfig: (
    cameraId: number,
    navigate: NavigateFunction
  ) => Promise<void>;
  setVlmDraft: (
    settings?: Partial<VlmSettings>,
    customAnalysisName?: string
  ) => void;
  resetVlmDraft: () => void;
  patchCameraVlmConfig: (
    cameraId: number,
    navigate: NavigateFunction
  ) => Promise<boolean>;
  resetEditCameraStore: () => void;
}

export const useEditCameraStore = create<EditCameraStore>((set, get) => ({
  selectedAlertId: "",
  selectedAlertType: "",
  setSelectedAlertId: (selectedAlertId: string) => set({ selectedAlertId }),
  setSelectedAlertType: (selectedAlertType: string) =>
    set({ selectedAlertType }),
  notesDialogEditCameraOpen: false,
  notesText: "",
  setNotesText: (notesText: string) => set({ notesText }),
  setNotesDialogEditCameraOpen: (notesDialogEditCameraOpen: boolean) =>
    set({ notesDialogEditCameraOpen }),
  streamChangesMade: false,
  setStreamChangesMade: (streamChangesMade: boolean) =>
    set({ streamChangesMade }),
  enveraUser:
    // ((userInfo?.group?.name === "Envera") || (userInfo?.permission?.super_user)) ? true :
    false,
  siteInfoEditCamera: {},
  setSiteInfoEditCamera: (siteInfoEditCamera: any) =>
    set({ siteInfoEditCamera }),
  chmActive: false,
  setChmActive: (chmActive: boolean) => set({ chmActive }),
  lineCrossingActive: { intruder: false, vehicle: false },
  setLineCrossingActive: (lineCrossingActive: {
    intruder: boolean;
    vehicle: boolean;
  }) => set({ lineCrossingActive }),
  // Line crossings data
  lineCrossingsData: [],
  setLineCrossingsData: (lineCrossingsData: LineCrossing[]) =>
    set({ lineCrossingsData }),
  lineCrossingsMetricLabels: [],
  setLineCrossingsMetricLabels: (lineCrossingsMetricLabels: MetricLabel[]) =>
    set({ lineCrossingsMetricLabels }),
  getLineCrossingsData: async (
    navigate: NavigateFunction,
    cameraId: number
  ) => {
    const AxiosUI = createApi(``);
    const {
      setLineCrossingsData,
      setLineCrossingsMetricLabels,
      getLineCrossingsData,
    } = get();

    const retryFunction = () => {
      getLineCrossingsData(navigate, cameraId);
    };

    try {
      // Fetch line crossings and metrics concurrently
      const [lineCrossingsResponse, metricsResponse] = await Promise.all([
        // Default (camera-wide) lines only; preset lines belong to their preset's view.
        AxiosUI.get(buildLineCrossingsListPath(cameraId)),
        AxiosUI.get(`/metric/?camera_id=${cameraId}`),
      ]);

      // Filter metrics to only include intruder_line_crossing, vehicle_line_crossing, bike_line_crossing
      const allowedLabels = [
        "intruder_line_crossing",
        "vehicle_line_crossing",
        "bike_line_crossing",
      ];
      const filteredMetrics = metricsResponse.data.results
        .filter((metric: any) => allowedLabels.includes(metric.label_name))
        .map((metric: any) => ({
          id: metric.label,
          label_name: metric.label_name,
        }));
      setLineCrossingsMetricLabels(filteredMetrics);

      // Process line crossings - assign colors to each line
      const lineCrossings = lineCrossingsResponse.data.results || [];
      const processedLines: LineCrossing[] = lineCrossings.map(
        (line: any, index: number) => {
          // Handle both legacy and new formats for direction
          let frontendDirection: LineDirection;
          if (isLegacyDirectionFormat(line.direction || "")) {
            frontendDirection = (line.direction as LineDirection) || "Both";
          } else {
            frontendDirection = backendToFrontendDirection(
              (line.direction as BackendLineDirection) || "both",
              line.coordinates
            );
          }

          return {
            id: line.id,
            camera: line.camera,
            metric_labels: line.metric_labels || [],
            coordinates: line.coordinates,
            direction: frontendDirection,
            color: LINE_COLORS[index % LINE_COLORS.length],
          };
        }
      );
      setLineCrossingsData(processedLines);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          // Silently fail - line crossings data is supplementary
          console.warn("Failed to load line crossings data");
        }
      );
    }
  },
  addHealthMonitoringDialogOpen: false,
  setAddHealthMonitoringDialogOpen: (addHealthMonitoringDialogOpen: boolean) =>
    set({ addHealthMonitoringDialogOpen }),
  chmValueChanged: false,
  deleteWebhookDialogOpen: false,
  setDeleteWebhookDialogOpen: (deleteWebhookDialogOpen: boolean) =>
    set({ deleteWebhookDialogOpen }),
  deleteAlertDialogOpen: false,
  setDeleteAlertDialogOpen: (deleteAlertDialogOpen: boolean) =>
    set({ deleteAlertDialogOpen }),
  webhookIdToDelete: "",
  setWebhookIdToDelete: (webhookIdToDelete: string) =>
    set({ webhookIdToDelete }),
  backdropLoadingScreenEditCameraOn: false,
  setBackdropLoadingScreenEditCameraOn: (
    backdropLoadingScreenEditCameraOn: boolean
  ) => set({ backdropLoadingScreenEditCameraOn }),
  setCHMValueChanged: (chmValueChanged: boolean) => set({ chmValueChanged }),
  showHealthMonitoring: false,
  setShowHealthMonitoring: (showHealthMonitoring: boolean) =>
    set({ showHealthMonitoring }),
  chmStreamMetric: {},
  setCHMStreamMetric: (chmStreamMetric: any) => set({ chmStreamMetric }),
  cameraStreams: null,
  setCameraStreams: (cameraStreams: EditCameraStore["cameraStreams"]) =>
    set({ cameraStreams }),
  cameraStreamsSelectedIndex: 0,
  setCameraStreamsSelectedIndex: (cameraStreamsSelectedIndex: number) =>
    set({ cameraStreamsSelectedIndex }),
  sysaidAlertsOnCamera: false,
  setSysaidAlertsOnCamera: (sysaidAlertsOnCamera: boolean) =>
    set({ sysaidAlertsOnCamera }),
  addSysaidAlertDialogOpen: false,
  setAddSysaidAlertDialogOpen: (addSysaidAlertDialogOpen: boolean) =>
    set({ addSysaidAlertDialogOpen }),
  webhooksData: null,
  setWebhooksData: (webhooksData: EditCameraStore["webhooksData"]) =>
    set({ webhooksData }),
  cameraStreamsObject: {} as Partial<CameraStreams>,
  setCameraStreamsObject: (cameraStreamsObject: Partial<CameraStreams>) =>
    set({ cameraStreamsObject }),
  cameraStreamsPatchObject: [] as Partial<CameraStreams>,
  setCameraStreamsPatchObject: (
    cameraStreamsPatchObject:
      | Partial<CameraStreams>
      | ((prev: Partial<CameraStreams>) => Partial<CameraStreams>)
  ) =>
    set((state) => ({
      cameraStreamsPatchObject:
        typeof cameraStreamsPatchObject === "function"
          ? cameraStreamsPatchObject(state.cameraStreamsPatchObject || [])
          : cameraStreamsPatchObject,
    })),
  updatedAlertStreamTypes: {
    immix: false,
    dice: false,
    sureview: false,
    sentinel: false,
    bold: false,
    patriot: false,
    softguard: false,
    sysaid: false,
    evalink: false,
  },
  updatedAlertStreams: {
    immix: {},
    dice: {},
    sureview: {},
    sentinel: {},
    bold: {},
    patriot: {},
    softguard: {},
    sysaid: {},
    evalink: {},
  },
  setUpdatedAlertStreamTypes: (
    updatedAlertStreamTypes:
      | UpdatedAlertStreamTypes
      | ((prev: UpdatedAlertStreamTypes) => UpdatedAlertStreamTypes)
  ) =>
    set((state) => ({
      updatedAlertStreamTypes:
        typeof updatedAlertStreamTypes === "function"
          ? updatedAlertStreamTypes(state.updatedAlertStreamTypes)
          : updatedAlertStreamTypes,
    })),
  setUpdatedAlertStreams: (
    updatedAlertStreams:
      | EditCameraStore["updatedAlertStreams"]
      | ((
          prev: EditCameraStore["updatedAlertStreams"]
        ) => EditCameraStore["updatedAlertStreams"])
  ) =>
    set((state) => ({
      updatedAlertStreams:
        typeof updatedAlertStreams === "function"
          ? updatedAlertStreams(state.updatedAlertStreams)
          : updatedAlertStreams,
    })),
  vCHOnlySite: false,
  setVCHOnlySite: (vCHOnlySite: boolean) => set({ vCHOnlySite }),
  slicingChangesMade: false,
  setSlicingChangesMade: (slicingChangesMade: boolean) =>
    set({ slicingChangesMade }),
  slicingChanges: [],
  setSlicingChanges: (slicingChanges: any[]) => set({ slicingChanges }),
  showSlicingWarningMessage: false,
  setShowSlicingWarningMessage: (showSlicingWarningMessage: boolean) =>
    set({ showSlicingWarningMessage }),
  vlmSettings: { ...DEFAULT_VLM_SETTINGS },
  vlmSettingsBackup: { ...DEFAULT_VLM_SETTINGS },
  vlmCustomCase: DEFAULT_CUSTOM_ANALYSIS_NAME,
  vlmCustomCaseBackup: DEFAULT_CUSTOM_ANALYSIS_NAME,
  vlmConfigLoading: false,
  vlmConfigLoaded: false,
  vlmConfigCameraId: null,
  vlmCaseValueMap: {},
  vlmChangesMade: false,
  setVlmChangesMade: (vlmChangesMade: boolean) => set({ vlmChangesMade }),
  fetchCameraVlmConfig: async (
    cameraId: number,
    navigate: NavigateFunction
  ) => {
    const { vlmConfigLoaded, vlmConfigCameraId } = get();
    if (vlmConfigLoaded && vlmConfigCameraId === cameraId) {
      return;
    }

    set({ vlmConfigLoading: true, vlmConfigCameraId: cameraId });

    const response = await getCameraVlmConfig(cameraId, navigate);

    if (!response) {
      set({ vlmConfigLoading: false });
      return;
    }

    const { singleImageModalityId, imageDiffModalityId } =
      getModalityIdsFromConfig(response.modalities);

    // vlm_case/ is superuser-only on the admin API; for everyone else the
    // three reads below only produced a 403 per Edit Camera open (SUP-688,
    // ENG-2014), so they are skipped and the case map stays empty.
    const canReadVlmCases = isSuperUserFromLocalStorage();
    const [singleImageCases, imageDiffCases, allCases] = await Promise.all([
      canReadVlmCases && singleImageModalityId
        ? getVlmCases(singleImageModalityId, navigate)
        : Promise.resolve([]),
      canReadVlmCases && imageDiffModalityId
        ? getVlmCases(imageDiffModalityId, navigate)
        : Promise.resolve([]),
      canReadVlmCases ? getAllVlmCases(navigate) : Promise.resolve([]),
    ]);

    const vlmCaseValueMap = resolveVlmCaseValueMap(
      response,
      singleImageCases,
      imageDiffCases,
      allCases
    );

    const uiState = mapVlmConfigToUi(response, vlmCaseValueMap);
    set({
      vlmSettings: uiState.settings,
      vlmSettingsBackup: { ...uiState.settings },
      vlmCustomCase: uiState.customAnalysisName,
      vlmCustomCaseBackup: uiState.customAnalysisName,
      vlmConfigLoading: false,
      vlmConfigLoaded: true,
      vlmCaseValueMap,
      vlmChangesMade: false,
    });
  },
  setVlmDraft: (
    settings?: Partial<VlmSettings>,
    customAnalysisName?: string
  ) => {
    set((state) => ({
      vlmSettings: settings
        ? { ...state.vlmSettings, ...settings }
        : state.vlmSettings,
      vlmCustomCase:
        customAnalysisName !== undefined
          ? customAnalysisName
          : state.vlmCustomCase,
      vlmChangesMade: true,
    }));
  },
  resetVlmDraft: () => {
    const { vlmSettingsBackup, vlmCustomCaseBackup } = get();
    set({
      vlmSettings: { ...vlmSettingsBackup },
      vlmCustomCase: vlmCustomCaseBackup,
      vlmChangesMade: false,
    });
  },
  patchCameraVlmConfig: async (
    cameraId: number,
    navigate: NavigateFunction
  ) => {
    const {
      vlmSettings,
      vlmSettingsBackup,
      vlmCustomCase,
      vlmCustomCaseBackup,
      vlmCaseValueMap,
    } = get();

    const patchBody = buildVlmConfigPatch(
      {
        settings: vlmSettingsBackup,
        customAnalysisName: vlmCustomCaseBackup,
      },
      {
        settings: vlmSettings,
        customAnalysisName: vlmCustomCase,
      },
      vlmCaseValueMap
    );

    if (!patchBody) {
      set({ vlmChangesMade: false });
      return true;
    }

    const response = await patchCameraVlmConfigRequest(
      cameraId,
      patchBody,
      navigate
    );

    if (!response) {
      return false;
    }

    const uiState = mapVlmConfigToUi(response, vlmCaseValueMap);
    set({
      vlmSettings: uiState.settings,
      vlmSettingsBackup: { ...uiState.settings },
      vlmCustomCase: uiState.customAnalysisName,
      vlmCustomCaseBackup: uiState.customAnalysisName,
      vlmChangesMade: false,
    });

    return true;
  },
  handleDeleteWebhook: async (
    navigate: NavigateFunction,
    webhookId: string
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handleDeleteWebhook(navigate, webhookId);
    };

    const { setBackdropLoadingScreenEditCameraOn } = get();
    // setBackdropLoadingScreenEditCameraOn(true);

    try {
      await AxiosUI.delete(`webhook/${webhookId}/`);
      // Stream webhooks ride settings.json (stream_webhooks) — needs a sync.
      markSiteUnsynced();
      get().getWebhooksData(
        navigate,
        Number(getNumbersAfterEditCamera(window.location.href))
      );
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Problem deleting Webhook. Please try again.");
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },
  getWebhooksData: async (navigate: NavigateFunction, cameraId: number) => {
    const AxiosUI = createApi(``);

    const { setWebhooksData, setBackdropLoadingScreenEditCameraOn } = get();

    const retryFunction = () => {
      get().getWebhooksData(navigate, cameraId);
    };

    try {
      const response = await AxiosUI.get(
        `/webhook/?stream__camera__id=${cameraId}`
      );
      if (response.data.count > 0) {
        setWebhooksData(response.data.results);
      }
      setBackdropLoadingScreenEditCameraOn(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },
  handleSubmitNewStreamWebhook: async (
    postObject: any,
    navigate: NavigateFunction,
    cameraId: number
  ) => {
    const AxiosUI = createApi(``);
    const { setBackdropLoadingScreenEditCameraOn } = get();
    const retryFunction = () => {
      get().handleSubmitNewStreamWebhook(postObject, navigate, cameraId);
    };

    try {
      await AxiosUI.post(`/webhook/add/`, postObject);
      markSiteUnsynced();
      const { getWebhooksData } = get();
      getWebhooksData(navigate, cameraId);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Error creating new Stream Webhook.  Please try again.");
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },
  handlePatchStreamWebhook: async (
    postObject: any,
    webhookId: number,
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().handlePatchStreamWebhook(postObject, webhookId, navigate);
    };

    const { getWebhooksData, setBackdropLoadingScreenEditCameraOn } = get();

    try {
      await AxiosUI.patch(`/webhook/${webhookId}/`, postObject);
      markSiteUnsynced();
      getWebhooksData(
        navigate,
        Number(getNumbersAfterEditCamera(window.location.href))
      );
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Error updating Stream Webhook.  Please try again.");
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },
  getCameraStreams: async (navigate: NavigateFunction, cameraId: number) => {
    const AxiosUI = createApi(``);

    const { setCameraStreams } = get();

    const retryFunction = () => {
      get().getCameraStreams(navigate, cameraId);
    };

    try {
      const response = await AxiosUI.get(
        `stream/product/?camera_id=${cameraId}`
      );
      if (response.data) {
        setCameraStreams(response.data);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },
  handlePatchCameraStreams: async (
    navigate: NavigateFunction,
    cameraId: number,
    setEditMode: any
  ) => {
    const AxiosUI = createApi(``);

    const {
      cameraStreamsPatchObject,
      setStreamChangesMade,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
      cameraStreamsObject,
      setBackdropLoadingScreenEditCameraOn,
      handlePatchCameraStreams,
    } = get();

    const retryFunction = () => {
      handlePatchCameraStreams(navigate, cameraId, setEditMode);
    };

    setBackdropLoadingScreenEditCameraOn(true);

    try {
      const response = await AxiosUI.patch(`camera/${cameraId}/general_info/`, {
        streams: cameraStreamsPatchObject,
      });
      // Stream config rides settings.json to the connector — the site now has
      // unsynced changes.
      markSiteUnsynced();
      setStreamChangesMade(false);
      setEditMode(false);
      setCameraStreamsPatchObject(response.data.streams);
      setCameraStreamsObject(response.data.streams);
      setBackdropLoadingScreenEditCameraOn(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setCameraStreamsPatchObject(cameraStreamsObject);
          alert("Error updating Stream.  Please try again.");
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },
  handleSubmitNewSysaidAlert: async (
    postObject: any,
    setCameraGeneralInfo: any,
    setCameraGeneralInfoBackup: any,
    setPatchObject: any,
    cameraGeneralInfoBackup: any,
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const {
      setBackdropLoadingScreenEditCameraOn,
      handleSubmitNewSysaidAlert,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    setBackdropLoadingScreenEditCameraOn(true);

    const retryFunction = () => {
      handleSubmitNewSysaidAlert(
        postObject,
        setCameraGeneralInfo,
        setCameraGeneralInfoBackup,
        setPatchObject,
        cameraGeneralInfoBackup,
        navigate
      );
    };

    try {
      const response = await AxiosUI.patch(
        `/camera/${getNumbersAfterEditCamera(location.href)}/general_info/`,
        { streams: postObject }
      );
      // New SysAid alert rides the streams in settings.json — needs a sync.
      markSiteUnsynced();
      setCameraGeneralInfo(response.data);
      setCameraGeneralInfoBackup(response.data);
      setCameraStreamsPatchObject(response.data.streams);
      setCameraStreamsObject(response.data.streams);
      setBackdropLoadingScreenEditCameraOn(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Error adding SysAid Alert.  Please try again.");
          setCameraGeneralInfo(cameraGeneralInfoBackup);
          setPatchObject({
            camera_name: cameraGeneralInfoBackup.camera_name,
            streams: cameraGeneralInfoBackup.streams,
          });
          setCameraStreamsPatchObject(cameraGeneralInfoBackup.streams);
          setCameraStreamsObject(cameraGeneralInfoBackup.streams);
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },
  handlePatchImmixAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchImmixAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchImmixAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.immix) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.immix);
      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.immix[alertId];
        try {
          await AxiosUI.patch(`/immix/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              immix: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              immix: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Immix Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  immix: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  immix: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchDiceAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchDiceAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchDiceAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.dice) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.dice);
      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.dice[alertId];
        try {
          await AxiosUI.patch(`/dice/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              dice: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              dice: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating a Dice Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  dice: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  dice: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchBoldAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchBoldAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchBoldAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.bold) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.bold);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.bold[alertId];
        try {
          await AxiosUI.patch(`/bold/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              bold: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              bold: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Immix Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  bold: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  bold: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchPatriotAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>,
    setPatriotAlerts: Dispatcher<any>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchPatriotAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchPatriotAlerts(navigate, setEditMode, setPatriotAlerts);
    };

    if (updatedAlertStreams.patriot) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.patriot);

      try {
        // Use Promise.all to properly await all PATCH operations
        await Promise.all(
          keys.map(async (key) => {
            const alertId = key;
            const alertData = updatedAlertStreams.patriot[alertId];
            await AxiosUI.patch(`/patriot/${alertId}/`, alertData);
            markSiteUnsynced();
          })
        );

        // All patches succeeded, now update state and refresh data
        setUpdatedAlertStreamTypes((prev: any) => ({
          ...prev,
          patriot: false,
        }));
        setUpdatedAlertStreams((prev: any) => ({
          ...prev,
          patriot: {},
        }));

        try {
          const response = await AxiosUI.get(
            `camera/${getNumbersAfterEditCamera(
              window.location.href
            )}/general_info/`
          );
          if (response.data) {
            setEditMode(false);
            setCameraStreamsPatchObject(response.data.streams);
            setCameraStreamsObject(response.data.streams);

            // Update patriotAlerts state with the refreshed data
            const patriotPushedArray: any[] = [];
            if (response.data.streams[0]?.patriot_alerts) {
              response.data.streams[0].patriot_alerts.forEach(
                (alertObject: any) => {
                  patriotPushedArray.push(alertObject);
                }
              );
            }
            setPatriotAlerts(patriotPushedArray);

            setBackdropLoadingScreenEditCameraOn(false);
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              setEditMode(false);
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Error updating a Patriot Alert.  Please try again.");
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              patriot: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              patriot: {},
            }));
            setBackdropLoadingScreenEditCameraOn(false);
          }
        );
      }
    }
  },
  handlePatchSureviewAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchSureviewAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchSureviewAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.sureview) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.sureview);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.sureview[alertId];
        try {
          await AxiosUI.patch(`/sureview/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              sureview: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              sureview: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Sureview Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  sureview: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  sureview: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchSentinelAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchSentinelAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchSentinelAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.sentinel) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.sentinel);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.sentinel[alertId];
        try {
          await AxiosUI.patch(`/sentinel/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              sentinel: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              sentinel: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Sentinel Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  sentinel: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  sentinel: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchSoftguardAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);

    const {
      setUpdatedAlertStreams,
      handlePatchSoftguardAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchSoftguardAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.softguard) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.softguard);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.softguard[alertId];
        try {
          await AxiosUI.patch(`/softguard/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              softguard: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              softguard: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Softguard Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  softguard: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  softguard: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchSysaidAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchSysaidAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchSysaidAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.sysaid) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.sysaid);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.sysaid[alertId];
        try {
          await AxiosUI.patch(`/sysaid/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              sysaid: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              sysaid: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an SysAid Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  sysaid: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  sysaid: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handlePatchEvalinkAlerts: async (
    navigate: NavigateFunction,
    setEditMode: Dispatcher<boolean>
  ) => {
    const AxiosUI = createApi(``);
    const {
      setUpdatedAlertStreams,
      handlePatchEvalinkAlerts,
      updatedAlertStreams,
      setUpdatedAlertStreamTypes,
      setBackdropLoadingScreenEditCameraOn,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
    } = get();

    const retryFunction = () => {
      handlePatchEvalinkAlerts(navigate, setEditMode);
    };

    if (updatedAlertStreams.evalink) {
      setBackdropLoadingScreenEditCameraOn(true);

      const keys = Object.keys(updatedAlertStreams.evalink);

      keys.forEach(async (key, index) => {
        const alertId = key;
        const alertData = updatedAlertStreams.evalink[alertId];
        try {
          await AxiosUI.patch(`/evalink/${alertId}/`, alertData);
          markSiteUnsynced();
          if (index === keys.length - 1) {
            setUpdatedAlertStreamTypes((prev: any) => ({
              ...prev,
              evalink: false,
            }));
            setUpdatedAlertStreams((prev: any) => ({
              ...prev,
              evalink: {},
            }));
            try {
              const response = await AxiosUI.get(
                `camera/${getNumbersAfterEditCamera(
                  window.location.href
                )}/general_info/`
              );
              if (response.data) {
                setEditMode(false);
                setCameraStreamsPatchObject(response.data.streams);
                setCameraStreamsObject(response.data.streams);
                setBackdropLoadingScreenEditCameraOn(false);
              }
            } catch (reason) {
              universalAPIErrorHandler(
                reason as AxiosError,
                navigate,
                retryFunction,
                () => {
                  setEditMode(false);
                  setBackdropLoadingScreenEditCameraOn(false);
                }
              );
            }
          }
        } catch (reason) {
          universalAPIErrorHandler(
            reason as AxiosError,
            navigate,
            retryFunction,
            () => {
              alert("Error updating an Evalink Alert.  Please try again.");
              if (index === keys.length - 1) {
                setUpdatedAlertStreamTypes((prev: any) => ({
                  ...prev,
                  evalink: false,
                }));
                setUpdatedAlertStreams((prev: any) => ({
                  ...prev,
                  evalink: {},
                }));
              }
              setBackdropLoadingScreenEditCameraOn(false);
            }
          );
        }
      });
    }
  },
  handleDeleteAlerts: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);
    const {
      setBackdropLoadingScreenEditCameraOn,
      handleDeleteAlerts,
      setCameraStreamsPatchObject,
      setCameraStreamsObject,
      selectedAlertId,
      selectedAlertType,
    } = get();

    const retryFunction = () => {
      handleDeleteAlerts(navigate);
    };

    setBackdropLoadingScreenEditCameraOn(true);

    try {
      await AxiosUI.delete(`/${selectedAlertType}/${selectedAlertId}/`);
      // Alert-integration configs ride settings.json — needs a sync.
      markSiteUnsynced();
      try {
        const response = await AxiosUI.get(
          `camera/${getNumbersAfterEditCamera(
            window.location.href
          )}/general_info/`
        );
        setCameraStreamsPatchObject(response.data.streams);
        setCameraStreamsObject(response.data.streams);
        setBackdropLoadingScreenEditCameraOn(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Problem getting updated data.  Please refresh the page.");
            setBackdropLoadingScreenEditCameraOn(false);
          }
        );
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          alert("Problem deleting Alert. Please try again.");
          setBackdropLoadingScreenEditCameraOn(false);
        }
      );
    }
  },

  handleCheckForVCHOnlySite: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);
    const { handleCheckForVCHOnlySite, setVCHOnlySite } = get();

    const retryFunction = () => {
      handleCheckForVCHOnlySite(navigate);
    };

    try {
      const response = await AxiosUI.get(
        `auto_patrol_schedule/by_customer/${getNumbersAfterSites(
          window.location.href
        )}/`
      );

      const hasAPSchedules = response.data.some(
        (schedule: any) => schedule.patrolType === "AutoPatrol"
      );

      if (!hasAPSchedules) {
        setVCHOnlySite(true);
      } else {
        setVCHOnlySite(false);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  handlePatchSlicing: async (
    streamId: number,
    slice: boolean,
    streamIndex: number,
    navigate: NavigateFunction,
    max_slices?: number
  ) => {
    const AxiosUI = createApi(``);
    const { setCameraStreamsObject } = get();

    const retryFunction = () => {
      get().handlePatchSlicing(
        streamId,
        slice,
        streamIndex,
        navigate,
        max_slices
      );
    };

    const patchData: { slice: boolean; max_slices?: number } = { slice };
    if (max_slices !== undefined) {
      patchData.max_slices = max_slices;
    }

    try {
      await AxiosUI.patch(`stream/${streamId}/`, patchData);
      // Slicing rides settings.json — same unsynced marking as the bulk
      // slicing dialog.
      markSiteUnsynced();
      const currentStreams = get().cameraStreamsObject as CameraStreams;
      const updatedStreams = currentStreams.map((stream, idx) =>
        idx === streamIndex
          ? {
              ...stream,
              slice,
              ...(max_slices !== undefined && { max_slices }),
            }
          : stream
      );
      setCameraStreamsObject(updatedStreams);
      return true;
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
      return false;
    }
  },

  resetEditCameraStore: () => {
    set({
      cameraStreamsSelectedIndex: 0,
      // Reset on mount so a previous camera's site can't be read as this one's
      // until getEditCameraData refills it.
      siteInfoEditCamera: {},
      sysaidAlertsOnCamera: false,
      addSysaidAlertDialogOpen: false,
      vCHOnlySite: false,
      backdropLoadingScreenEditCameraOn: false,
      lineCrossingActive: { intruder: false, vehicle: false },
      lineCrossingsData: [],
      lineCrossingsMetricLabels: [],
      showSlicingWarningMessage: false,
      vlmSettings: { ...DEFAULT_VLM_SETTINGS },
      vlmSettingsBackup: { ...DEFAULT_VLM_SETTINGS },
      vlmCustomCase: DEFAULT_CUSTOM_ANALYSIS_NAME,
      vlmCustomCaseBackup: DEFAULT_CUSTOM_ANALYSIS_NAME,
      vlmConfigLoading: false,
      vlmConfigLoaded: false,
      vlmConfigCameraId: null,
      vlmCaseValueMap: {},
      vlmChangesMade: false,
    });
  },
}));
